import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks, LOGO_URL } from '../data/business';
import { instagramPreviewImages, facebookPreviewImage } from '../data/social';

// Instagram icon SVG
const InstagramIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// Facebook icon SVG
const FacebookIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export function SocialMedia() {
  const { t, isRTL } = useLanguage();

  return (
    <section 
      id="social" 
      className="py-24 sm:py-32 bg-[#FDF8F4] overflow-hidden"
      data-testid="social-media-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100 }}
            className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6B1C23] mb-4 ${
              isRTL ? 'font-noto-serif-arabic' : ''
            }`}
            data-testid="social-title"
          >
            {t('social.title')}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 w-24 bg-[#6B1C23] mx-auto mb-4"
          />
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className={`font-manrope text-[#6B6B6B] text-lg ${isRTL ? 'font-cairo' : ''}`}
          >
            {t('social.subtitle')}
          </motion.p>
        </motion.div>

        {/* Social Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Instagram Card */}
          <motion.a
            href={socialLinks.instagram}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ scale: 1.03, y: -5 }}
            className="group relative bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] p-1 rounded-2xl"
            data-testid="instagram-link"
          >
            <div className="bg-white rounded-2xl p-8 h-full">
              <div className="flex items-center gap-4 mb-6">
                <motion.div 
                  className="text-[#E1306C]"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <InstagramIcon />
                </motion.div>
                <div>
                  <h3 className={`font-cormorant text-2xl font-semibold text-[#333] ${isRTL ? 'font-noto-serif-arabic' : ''}`}>
                    Instagram
                  </h3>
                  <p className="font-manrope text-[#E1306C] font-medium">
                    {t('social.instagramHandle')}
                  </p>
                </div>
              </div>
              
              {/* Fake Instagram Posts Preview */}
              <div className="grid grid-cols-3 gap-2 mb-6">
                {instagramPreviewImages.map((src, i) => (
                  <motion.div
                    key={`ig-${i}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="aspect-square overflow-hidden rounded-lg"
                  >
                    <img 
                      src={src} 
                      alt={`Instagram post ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </motion.div>
                ))}
              </div>

              <motion.div 
                className="flex items-center justify-center gap-2 text-[#E1306C] font-manrope font-semibold"
                whileHover={{ x: 5 }}
              >
                {t('social.instagram')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.div>
            </div>
          </motion.a>

          {/* Facebook Card */}
          <motion.a
            href={socialLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            whileHover={{ scale: 1.03, y: -5 }}
            className="group relative bg-[#1877F2] p-1 rounded-2xl"
            data-testid="facebook-link"
          >
            <div className="bg-white rounded-2xl p-8 h-full">
              <div className="flex items-center gap-4 mb-6">
                <motion.div 
                  className="text-[#1877F2]"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <FacebookIcon />
                </motion.div>
                <div>
                  <h3 className={`font-cormorant text-2xl font-semibold text-[#333] ${isRTL ? 'font-noto-serif-arabic' : ''}`}>
                    Facebook
                  </h3>
                  <p className="font-manrope text-[#1877F2] font-medium">
                    {t('social.facebookHandle')}
                  </p>
                </div>
              </div>
              
              {/* Facebook Preview */}
              <div className="bg-[#F0F2F5] rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src={LOGO_URL}
                    alt="Salam Grill"
                    className="w-10 h-10 rounded-full object-contain bg-white p-1"
                  />
                  <div>
                    <p className="font-manrope font-semibold text-[#333] text-sm">Salam Grill</p>
                    <p className="font-manrope text-[#65676B] text-xs">Richardson, TX</p>
                  </div>
                </div>
                <p className="font-manrope text-[#333] text-sm mb-3">
                  Fresh off the charcoal grill! Our authentic Mediterranean BBQ is made with love and tradition. Visit us today!
                </p>
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={facebookPreviewImage}
                    alt="Featured dish"
                    className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                  <span className="text-[#65676B] text-sm flex items-center gap-1">
                    <span className="text-blue-500">👍</span> 248
                  </span>
                  <span className="text-[#65676B] text-sm">42 comments</span>
                  <span className="text-[#65676B] text-sm">18 shares</span>
                </div>
              </div>

              <motion.div 
                className="flex items-center justify-center gap-2 text-[#1877F2] font-manrope font-semibold"
                whileHover={{ x: 5 }}
              >
                {t('social.facebook')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </motion.div>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
